<script lang="ts">
import type { ColorPresetInput } from '#layers/ui/utils/colors';
import { tv } from 'tailwind-variants';

const colorPresetPicker = tv({
	slots: {
		root: 'flex flex-col gap-3',
		customButton: `
			ms-auto flex size-6 shrink-0 items-center justify-center rounded-full
			bg-[conic-gradient(var(--stop-0),var(--stop-1),var(--stop-2),var(--stop-3),var(--stop-4),var(--stop-5),var(--stop-0))]
			transition-all
			hover:scale-110
			focus:outline-none
			focus-visible:ring-1 focus-visible:ring-primary
			disabled:cursor-not-allowed disabled:opacity-50
		`,
		customButtonActive: 'scale-110 ring-1 ring-primary',
		customPanel: 'flex flex-col gap-3',
		backButton: `
			flex items-center gap-1 text-xs text-muted
			hover:text-highlighted
			disabled:cursor-not-allowed disabled:opacity-50
		`,
	},
});

export interface ColorPresetPickerProps {
	presets?: ColorPresetInput[];
	allowCustom?: boolean;
	defaultCustomColor?: string;
	disabled?: boolean;
	ui?: Partial<typeof colorPresetPicker.slots> & {
		swatches?: string;
		swatch?: string;
	};
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAppConfig } from '#app/config';
import { DEFAULT_COLORS, isColorValid, normalizeColorPreset } from '#layers/ui/utils/colors';

const props = withDefaults(defineProps<ColorPresetPickerProps>(), {
	presets: () => [...DEFAULT_COLORS],
});

const modelValue = defineModel<string | null>();

const appConfig = useAppConfig();
const showFullColorPicker = ref(false);

const normalizedPresets = computed(() => props.presets.map((preset) => normalizeColorPreset(preset)));
const selectedPresetValues = computed(() => new Set(normalizedPresets.value.map((preset) => preset.value)));
const customButtonStyle = Object.fromEntries(DEFAULT_COLORS.map((color, index) => [`--stop-${index}`, color]));

const isCustomValue = computed(() => {
	const value = modelValue.value;

	return Boolean(value) && !selectedPresetValues.value.has(value!) && isColorValid(value!);
});

const resolvedDefaultCustomColor = computed(() => {
	return props.defaultCustomColor
		?? normalizedPresets.value.find((preset) => preset.color && isColorValid(preset.color))?.color
		?? DEFAULT_COLORS[0];
});

watch([() => modelValue.value, () => props.allowCustom], ([value, allowCustom], oldValues) => {
	const prevAllowCustom = oldValues?.[1];

	// When toggling allowCustom off, drop lingering custom colors that are no longer reachable via the preset grid.
	if (prevAllowCustom && !allowCustom && value && !selectedPresetValues.value.has(value)) {
		modelValue.value = null;
		showFullColorPicker.value = false;
		return;
	}

	if (!allowCustom) {
		showFullColorPicker.value = false;
		return;
	}

	if (value && selectedPresetValues.value.has(value)) {
		showFullColorPicker.value = false;
		return;
	}

	showFullColorPicker.value = value !== null && value !== undefined && value !== '' && isColorValid(value);
}, {
	immediate: true,
});

function handlePresetSelect(value: string | null | undefined) {
	modelValue.value = value ?? null;
}

function updateCustomColor(value?: string) {
	modelValue.value = value ?? null;
}

function openCustomColorPicker() {
	if (props.disabled) return;
	showFullColorPicker.value = true;
}

function closeCustomColorPicker() {
	if (props.disabled) return;
	showFullColorPicker.value = false;
}

function resolveTheme() {
	return colorPresetPicker();
}
</script>

<template>
	<div :class="resolveTheme().root({ class: [props.ui?.root] })">
		<DColorSwatches
			v-if="!showFullColorPicker"
			:model-value
			:presets="normalizedPresets"
			:disabled="props.disabled"
			:ui="{ root: props.ui?.swatches, swatch: props.ui?.swatch }"
			@update:model-value="handlePresetSelect"
		>
			<template v-if="allowCustom" #trailing>
				<button
					type="button"
					:disabled="props.disabled"
					:aria-label="$t('color_preset_picker.show_color_picker')"
					:aria-pressed="isCustomValue"
					:class="resolveTheme().customButton({ class: [props.ui?.customButton, isCustomValue ? resolveTheme().customButtonActive() : undefined] })"
					:style="customButtonStyle"
					@click="openCustomColorPicker"
				/>
			</template>
		</DColorSwatches>

		<div v-else-if="allowCustom" :class="resolveTheme().customPanel({ class: [props.ui?.customPanel] })">
			<div class="flex items-center justify-between">
				<button
					type="button"
					:disabled="props.disabled"
					:class="resolveTheme().backButton({ class: [props.ui?.backButton] })"
					@click="closeCustomColorPicker"
				>
					<DIcon :name="appConfig.ui.icons.back" class="size-3" />
					{{ $t('color_preset_picker.back_to_swatches') }}
				</button>
			</div>

			<DColorPicker
				:model-value="isCustomValue ? modelValue ?? undefined : undefined"
				:default-value="isCustomValue ? modelValue ?? undefined : resolvedDefaultCustomColor"
				:disabled="props.disabled"
				orientation="horizontal"
				:ui="{ selector: 'w-full', track: 'flex-1' }"
				@update:model-value="updateCustomColor"
			/>
		</div>
	</div>
</template>
